import {
  DialogHeader,
  DialogFooter,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axiosInstance from "@/utils/axiosInstance";
import React, { FC, useEffect, useState } from "react";
import { useRefresh } from "react-admin";
import { toast } from "sonner";
import { Orders, User } from "@/interface/entityInterface";
import { Info } from "lucide-react";

interface Props {
  order: Orders;
  drivers: User[];
}

const AssignDriver: FC<Props> = ({ order, drivers }) => {
  // TODO KL: use strong types, return flat array of drivers from /drivers endpoint
  // flatten drivers array, keep firstname and lastname from the main object and the data from the inner drivers array;
  const driversArray = drivers.flatMap((item) => item.drivers);

  const driversSort = driversArray.sort((a, b) =>
    a.status > b.status ? 1 : a.status < b.status ? -1 : 0,
  );

  const [openDialog, setOpenDialog] = React.useState(false);
  const [currDriver, setCurrDriver] = useState<number | string>(order.driver_id || "");
  const [pendingDriver, setPendingDriver] = React.useState<number | null>(null);
  const refresh = useRefresh();
  const [driverStatus, setDriverStatus] = useState<
    "AVAILABLE" | "BUSY" | "OFFLINE" | undefined
  >(undefined);

  const handleAssignDriver = (value: string) => {
    setPendingDriver(Number(value));
    setOpenDialog(true);
  };

  useEffect(() => {
    const foundDriverStatus = driversArray.find(
      (driver) => driver.id === pendingDriver,
    )?.status;
    setDriverStatus(foundDriverStatus);
  }, [pendingDriver]);

  const confirmAssignment = async () => {
    if (pendingDriver) {
      try {
        const res = await axiosInstance.put(
          `/taxi-orders/assign-driver/${order.id}`,
          {
            driverId: pendingDriver,
          },
        );
        if (res.data) {
          setOpenDialog(false);
          setPendingDriver(null);
          refresh();
          toast("Driver assigned successfully", {
            description: "Driver assigned successfully to this order!",
          });
        }
      } catch (e: any) {
        toast(e.response.data.message);
      }
    }
    setCurrDriver(pendingDriver);
    setPendingDriver(null);
    setOpenDialog(false);
  };

  return (
    <>
      <Label htmlFor={`${order.id}-driver`} className="sr-only">
        Drivers
      </Label>
      <Select
        onValueChange={handleAssignDriver}
        value={String(currDriver) || ("" as any)}
      >
        <SelectTrigger
          className={`h-8 w-44 bg-muted text-muted-foreground italic`}
          id={`${order.id}-order`}
        >
          <SelectValue placeholder="Select a driver" />
        </SelectTrigger>
        <SelectContent align="end">
          {driversSort?.map((item) => (
            <SelectItem
              key={item.id}
              value={String(item.id)}
              className="flex items-center justify-between"
            >
              {item.name}{" "}{item.vehicle?.nickname}
              <div
                className={`w-2 h-2 rounded-full ${item.status !== "AVAILABLE" ? "bg-red-600" : "bg-green-600"}`}
              />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <div className="flex items-center gap-x-2">
              {driverStatus !== "AVAILABLE" && <Info className="w-8 h-8" />}
              <DialogTitle>Confirm Assignment</DialogTitle>
            </div>
            <DialogDescription>
              {driverStatus === "AVAILABLE"
                ? "Do you want to assign this driver?"
                : "The selected driver is not available. Are you sure you want to assign this order to them? They will not be notified"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPendingDriver(null);
                setOpenDialog(false);
              }}
            >
              No
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmAssignment();
              }}
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssignDriver;
